import { z } from 'zod'
import { LOCALES } from './i18n.js'

/**
 * Player feedback: a short message and/or a 1-5 score, sent without leaving the
 * page the player is on. Anonymous visitors can send it too.
 */

export const FEEDBACK_KINDS = ['idea', 'bug', 'praise', 'other'] as const
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number]

export const FEEDBACK_MESSAGE_MAX = 2000

export const FeedbackSchema = z
  .object({
    kind: z.enum(FEEDBACK_KINDS).default('other'),
    /** Satisfaction score after a game. */
    rating: z.number().int().min(1).max(5).optional(),
    message: z.string().trim().max(FEEDBACK_MESSAGE_MAX).default(''),
    /** Optional: where to answer (email, handle…). Never required. */
    contact: z.string().trim().max(120).optional(),
    /** Path of the page the feedback was sent from. */
    page: z.string().max(200).optional(),
    locale: z.enum(LOCALES).optional(),
    viewport: z.enum(['mobile', 'desktop']).optional(),
    /** Game the score is about (sent after a game). */
    gameId: z.string().max(64).optional(),
    /** Honeypot: invisible to people, filled in by form-filling bots. */
    website: z.string().max(200).optional(),
  })
  .refine((f) => f.message.length > 0 || f.rating !== undefined, { message: 'feedback_empty', path: ['message'] })

export type FeedbackInput = z.infer<typeof FeedbackSchema>

/** What the server keeps (and forwards to the publisher). */
export interface FeedbackRecord {
  id: string
  createdAt: string
  kind: FeedbackKind
  rating: number | null
  message: string
  contact: string | null
  /** Account that sent it, if signed in. */
  playerId: string | null
  playerName: string | null
  page: string | null
  locale: string | null
  viewport: string | null
  gameId: string | null
  userAgent: string | null
}

export interface FeedbackResponse {
  ok: true
  id: string
}
